import { useMemo } from "react"
import type { WordCloudTerm } from "~/ui-rtk/api/commentModerationApi"

/**
 * A weighted-text cloud of the terms in the judged comments. Size follows
 * frequency (sqrt-scaled so one runaway term does not flatten the rest);
 * colour follows the verdict split: rose when the term mostly appears in
 * hidden comments, emerald when mostly in kept ones, gray in between. Pure
 * CSS flex-wrap, no layout library — it reads as a cloud and stays legible.
 */
export default function CommentWordCloud({
  terms,
  isLoading,
}: {
  terms: WordCloudTerm[] | undefined
  isLoading: boolean
}) {
  const items = useMemo(() => {
    if (!terms?.length) return []
    const max = Math.sqrt(terms[0].count)
    const min = Math.sqrt(terms[terms.length - 1].count)
    const scale = (count: number) => {
      if (max === min) return 1
      return 0.75 + ((Math.sqrt(count) - min) / (max - min)) * 1.9
    }
    // Interleave big and small terms so the large ones spread across the
    // cloud instead of stacking on the first line.
    const sorted = [...terms]
    const mixed: WordCloudTerm[] = []
    while (sorted.length) {
      mixed.push(sorted.shift() as WordCloudTerm)
      if (sorted.length) mixed.push(sorted.pop() as WordCloudTerm)
    }
    return mixed.map(t => {
      const hiddenShare = t.count ? t.hidden / t.count : 0
      let cls = "text-system-gray-200"
      if (hiddenShare >= 0.66) cls = "text-rose-300"
      else if (hiddenShare <= 0.33) cls = "text-emerald-300"
      return { ...t, size: scale(t.count), cls }
    })
  }, [terms])

  if (isLoading && !terms) {
    return <p className="text-sm text-system-gray-400">Loading word cloud…</p>
  }
  if (items.length === 0) {
    return (
      <p className="text-sm text-system-gray-400">
        No judged comments in this window yet.
      </p>
    )
  }
  return (
    <div className="flex flex-wrap items-baseline justify-center gap-x-3 gap-y-1 px-2 py-3 leading-none">
      {items.map(t => (
        <span
          key={t.term}
          className={`${t.cls} cursor-default whitespace-nowrap`}
          style={{
            fontSize: `${t.size}rem`,
            opacity: 0.6 + Math.min(0.4, t.size / 5),
          }}
          title={`"${t.term}" — ${t.count} comment${t.count === 1 ? "" : "s"} (${t.hidden} hidden, ${t.kept} kept)`}
        >
          {t.term}
        </span>
      ))}
    </div>
  )
}
